/**
 * Vips Preview - wasm-vips integration for real-time photo adjustments
 *
 * Implements the same algorithms as pyvips on the server (Sati v2 spec)
 * to ensure SSIM > 0.99 parity between browser preview and server output.
 *
 * Adjustment algorithms match docs/architecture/04-sati-v2.md exactly.
 */

let vipsInstance = null;
let vipsLoading = null;
let vipsUnavailable = false;

/**
 * Initialize wasm-vips library
 * Note: wasm-vips requires SharedArrayBuffer which needs COOP/COEP headers.
 * In environments without these headers, we gracefully disable client-side
 * preview and rely on server-side processing (Sati).
 *
 * @returns {Promise<Vips|null>} The initialized vips instance, or null if unavailable
 */
export async function initVips() {
  // If we already know it's unavailable, don't retry
  if (vipsUnavailable) return null;

  if (vipsInstance) return vipsInstance;

  if (vipsLoading) return vipsLoading;

  vipsLoading = (async () => {
    try {
      // Check if SharedArrayBuffer is available (requires COOP/COEP headers)
      if (typeof SharedArrayBuffer === 'undefined') {
        console.warn(
          'wasm-vips: SharedArrayBuffer not available. ' +
            'Client-side preview disabled. Adjustments will be applied server-side.'
        );
        vipsUnavailable = true;
        return null;
      }

      const Vips = await import('wasm-vips');
      vipsInstance = await Vips.default({
        // Use CDN for WASM files to avoid bundling issues
        locateFile: fileName => `https://cdn.jsdelivr.net/npm/wasm-vips@0.0.16/lib/${fileName}`,
      });
      // Limit cache to prevent OOM in WASM heap
      vipsInstance.Cache.maxMem(100 * 1024 * 1024); // 100MB
      vipsInstance.Cache.max(200);
      return vipsInstance;
    } catch (err) {
      // SharedArrayBuffer errors or other initialization failures
      console.warn(
        'wasm-vips initialization failed. ' +
          'Client-side preview disabled. Adjustments will be applied server-side.',
        err.message
      );
      vipsUnavailable = true;
      vipsLoading = null;
      return null;
    }
  })();

  return vipsLoading;
}

/**
 * Check if wasm-vips is available
 */
export function isVipsAvailable() {
  return vipsInstance !== null;
}

// =============================================================================
// LCH Phase — exposure, tone sliders, vibrance, saturation
// Pure sRGB math — no colourspace conversions.
// Matches Sati's _apply_lch_phase() exactly.
// =============================================================================

/**
 * max(0, x) using pure math — no relational_const needed.
 * Formula: max(0, x) = (x + abs(x)) / 2
 */
function maxZero(band) {
  return band.add(band.abs()).divide(2);
}

/**
 * Clamp a single-band image to [min, max] using pure math.
 * No relational_const — works in all vips bindings including wasm-vips.
 */
function clampBand(band, min, max) {
  // max(min, x): shift so min→0, apply maxZero, shift back
  let result = maxZero(band.subtract(min)).add(min);
  // min(max, x) on the already-clamped result
  result = result.subtract(maxZero(result.subtract(max)));
  return result;
}

/**
 * Apply exposure, tone sliders, vibrance, and saturation in sRGB.
 * Memory-efficient: uses image.linear() and .cast('uchar') instead of
 * per-band clampBand() to minimize wasm-vips intermediate images.
 *
 * @param {VipsImage} image - Input sRGB image (uchar)
 * @returns {VipsImage} Adjusted sRGB image (uchar)
 */
function applySrgbPhase(image, adj) {
  const exposure = adj.exposure || 0;
  const highlights = adj.highlights || 0;
  const shadows = adj.shadows || 0;
  const whites = adj.whites || 0;
  const blacks = adj.blacks || 0;
  const vibrance = adj.vibrance || 0;
  const saturation = adj.saturation || 0;

  const needsWork = exposure || highlights || shadows || whites || blacks || vibrance || saturation;
  if (!needsWork) return image;

  let result = image;

  // Helper: compute 1-band luma from 3-band image (3 intermediates)
  function getLuma(img) {
    return img.extractBand(0).multiply(0.299)
      .add(img.extractBand(1).multiply(0.587))
      .add(img.extractBand(2).multiply(0.114));
  }

  // --- Exposure: single linear op, cast clamps ---
  if (exposure) {
    const f = Math.pow(2, exposure);
    result = result.linear([f, f, f], [0, 0, 0]).cast('uchar');
  }

  // --- Highlights: scale around 128 for bright pixels ---
  // k = 1 + (hiF-1) * max(0, (luma-128)/128)
  // k is 1 where luma<=128 (no change), >1 where bright
  // pixel_new = (pixel - 128) * k + 128
  if (highlights) {
    const lu = getLuma(result.cast('float'));
    const t = maxZero(lu.subtract(128)).divide(128); // 0..1 weight
    const hiF = 1.0 + (highlights / 100) * 0.8;
    const k = t.multiply(hiF - 1).add(1); // 1-band scale factor
    // Apply k to all 3 bands: (pixel-128)*k+128 = pixel*k + 128*(1-k)
    const k3 = k.bandjoin([k, k]); // 3-band k
    result = result.cast('float').subtract(128).multiply(k3).add(128).cast('uchar');
  }

  // --- Shadows: lift dark pixels ---
  // lift = (128-luma) * (shF-1) * max(0, (128-luma)/128)
  if (shadows) {
    const lu = getLuma(result.cast('float'));
    const t = maxZero(lu.multiply(-1).add(128)).divide(128); // 0..1 weight
    const shF = 1.0 + (shadows / 100) * 0.7;
    const lift = lu.multiply(-1).add(128).multiply(shF - 1).multiply(t); // 1-band lift
    const lift3 = lift.bandjoin([lift, lift]); // 3-band
    result = result.cast('float').add(lift3).cast('uchar');
  }

  // --- Whites: brighten/darken the brightest pixels ---
  // Simple approach: linear boost above threshold via exposure-like multiply
  if (whites) {
    const whF = 1.0 + (whites / 100) * 0.6;
    // Apply as contrast around white point (224)
    const a = whF;
    const b = 224 * (1 - whF);
    result = result.linear([a, a, a], [b, b, b]).cast('uchar');
  }

  // --- Blacks: crush/lift the darkest pixels ---
  if (blacks) {
    const bkF = 1.0 + (blacks / 100) * 0.6;
    // Apply as contrast around black point (32)
    const a = bkF;
    const b = 32 * (1 - bkF);
    result = result.linear([a, a, a], [b, b, b]).cast('uchar');
  }

  // --- Saturation: blend toward luma ---
  if (saturation) {
    const f = result.cast('float');
    const lu = getLuma(f);
    const lu3 = lu.bandjoin([lu, lu]);
    // factor: 0 at -100 (grayscale), 1 at 0 (unchanged), 2 at +100
    const factor = Math.max(0, 1.0 + saturation / 100);
    result = f.multiply(factor).add(lu3.multiply(1 - factor)).cast('uchar');
  }

  // --- Vibrance: weaker saturation boost ---
  if (vibrance) {
    const f = result.cast('float');
    const lu = getLuma(f);
    const lu3 = lu.bandjoin([lu, lu]);
    const factor = 1.0 + (vibrance / 100) * 0.5;
    result = f.multiply(factor).add(lu3.multiply(1 - factor)).cast('uchar');
  }

  return result;
}

// =============================================================================
// sRGB Phase — contrast, clarity, temperature, tint
// These use image.linear() which is identical in wasm-vips and pyvips.
// =============================================================================

/**
 * Apply contrast — linear pivot at 128. Matches Sati apply_contrast().
 */
function applyContrast(image, amount) {
  if (amount === 0) return image;
  const c = 1 + amount / 100;
  const offset = (1 - c) * 128;
  return image.linear([c, c, c], [offset, offset, offset]);
}

/**
 * Apply clarity — contrast boost with 0.3x scale. Matches Sati apply_clarity().
 */
function applyClarity(image, amount) {
  if (amount === 0) return image;
  const c = 1 + (amount / 100) * 0.3;
  const offset = (1 - c) * 128;
  return image.linear([c, c, c], [offset, offset, offset]);
}

/**
 * Apply temperature — per-channel R/B multipliers. Matches Sati apply_temperature().
 */
function applyTemperature(image, amount) {
  if (amount === 0) return image;
  const shift = (amount / 100) * 0.15;
  return image.linear([1 + shift, 1, 1 - shift], [0, 0, 0]);
}

/**
 * Apply tint — green channel multiplier. Matches Sati apply_tint().
 */
function applyTint(image, amount) {
  if (amount === 0) return image;
  const shift = (amount / 100) * 0.1;
  return image.linear([1, 1 - shift, 1], [0, 0, 0]);
}

// =============================================================================
// Advanced Adjustments (Phase 2.2)
// =============================================================================

/**
 * Generate lookup table from control points using cubic spline interpolation
 *
 * @param {Array<[number, number]>} points - Control points [[x, y], ...] where x,y in 0-255
 * @returns {Uint8Array} 256-element LUT
 */
function generateLutFromPoints(points) {
  // Sort points by x value
  const sorted = [...points].sort((a, b) => a[0] - b[0]);

  // Ensure we have endpoints
  if (sorted[0][0] !== 0) sorted.unshift([0, 0]);
  if (sorted[sorted.length - 1][0] !== 255) sorted.push([255, 255]);

  // Build LUT using monotonic cubic interpolation
  const lut = new Uint8Array(256);

  for (let x = 0; x < 256; x++) {
    // Find surrounding control points
    let i = 0;
    while (i < sorted.length - 1 && sorted[i + 1][0] < x) i++;

    if (i >= sorted.length - 1) {
      lut[x] = Math.max(0, Math.min(255, sorted[sorted.length - 1][1]));
      continue;
    }

    const [x0, y0] = sorted[i];
    const [x1, y1] = sorted[i + 1];

    if (x1 === x0) {
      lut[x] = Math.max(0, Math.min(255, y0));
      continue;
    }

    // Linear interpolation between control points
    // For production, could use Catmull-Rom spline for smoother curves
    const t = (x - x0) / (x1 - x0);
    const y = y0 + t * (y1 - y0);
    lut[x] = Math.max(0, Math.min(255, Math.round(y)));
  }

  return lut;
}

/**
 * Apply tone curve adjustment using lookup table
 * Algorithm matches pyvips img.maplut() approach
 *
 * @param {Vips} vips - Vips instance
 * @param {VipsImage} image - Input image
 * @param {Array<[number, number]>} points - Control points [[x, y], ...]
 * @returns {VipsImage} Adjusted image
 */
function applyToneCurve(vips, image, points) {
  if (!points || points.length < 1) return image;

  const lut = generateLutFromPoints(points);

  // Create LUT image as PGM (Portable GrayMap) — avoids newFromMemory API issues.
  // PGM is a trivial binary image format that libvips/wasm-vips loads natively.
  const header = 'P5\n256 1\n255\n';
  const headerBytes = new TextEncoder().encode(header);
  const pgm = new Uint8Array(headerBytes.length + 256);
  pgm.set(headerBytes, 0);
  pgm.set(new Uint8Array(lut), headerBytes.length);

  let lutImage;
  try {
    lutImage = vips.Image.newFromBuffer(pgm);
  } catch (e) {
    console.error('Tone curve LUT creation failed:', e);
    return image;
  }

  // Apply LUT to each band (same LUT for R, G, B)
  return image.maplut(lutImage);
}

/**
 * Apply lift/gamma/gain color grading
 * Algorithm matches pyvips implementation for browser/server parity
 *
 * Lift: Adds offset to shadows (0 = neutral, -1 to +1 range)
 * Gamma: Mid-tone adjustment via power function (1 = neutral)
 * Gain: Multiplier for highlights (1 = neutral)
 *
 * Formula: out = gain * (in + lift)^(1/gamma)
 * Simplified for linear ops: out = gain * in + lift * 128
 *
 * @param {VipsImage} image - Input image
 * @param {object} lift - {r, g, b} values (-1 to +1)
 * @param {object} gamma - {r, g, b} values (0.1 to 3.0)
 * @param {object} gain - {r, g, b} values (0 to 3)
 * @returns {VipsImage} Adjusted image
 */
function applyColorGrading(image, lift, gamma, gain) {
  // Default neutral values
  const l = { r: 0, g: 0, b: 0, ...lift };
  const gm = { r: 1, g: 1, b: 1, ...gamma };
  const gn = { r: 1, g: 1, b: 1, ...gain };

  // Check if all neutral
  const isNeutral =
    l.r === 0 &&
    l.g === 0 &&
    l.b === 0 &&
    gm.r === 1 &&
    gm.g === 1 &&
    gm.b === 1 &&
    gn.r === 1 &&
    gn.g === 1 &&
    gn.b === 1;

  if (isNeutral) return image;

  // For browser performance, use linear approximation
  // Full gamma would require pow() per pixel which is expensive
  // Approximation: out = gain * in + lift * 128
  // Gamma effect approximated by adjusting gain based on gamma deviation

  // Approximate gamma effect on gain
  const effectiveGain = {
    r: gn.r * (1 / gm.r),
    g: gn.g * (1 / gm.g),
    b: gn.b * (1 / gm.b),
  };

  // Lift maps to offset (lift * 128 gives reasonable range)
  const offset = {
    r: l.r * 128,
    g: l.g * 128,
    b: l.b * 128,
  };

  return image.linear(
    [effectiveGain.r, effectiveGain.g, effectiveGain.b],
    [offset.r, offset.g, offset.b]
  );
}

/**
 * Apply sharpening using unsharp mask technique
 * Algorithm matches pyvips implementation
 *
 * Unsharp mask: out = in + amount * (in - blur(in))
 *
 * @param {VipsImage} image - Input image
 * @param {number} amount - Sharpening amount (0 to 3, typical 0.5-1.5)
 * @param {number} radius - Blur radius in pixels (0.1 to 3, default 0.5)
 * @returns {VipsImage} Adjusted image
 */
function applySharpening(image, amount, radius = 0.5) {
  if (amount === 0) return image;

  // Clamp parameters
  const clampedAmount = Math.max(0, Math.min(3, amount));
  const clampedRadius = Math.max(0.1, Math.min(3, radius));

  // Create blurred version (gaussian blur)
  // sigma = radius for approximate match
  const blurred = image.gaussblur(clampedRadius);

  // Unsharp mask: original + amount * (original - blurred)
  // Rewritten as: original * (1 + amount) - blurred * amount
  // Using linear(): out = a * in + b
  // We need to combine two images, so use subtract and linear

  // highpass = original - blurred
  const highpass = image.subtract(blurred);

  // sharpened = original + amount * highpass
  // = original + highpass.linear([amount], [0])
  const scaled = highpass.linear([clampedAmount, clampedAmount, clampedAmount], [0, 0, 0]);

  return image.add(scaled);
}

// =============================================================================
// Image Loading Helpers
// =============================================================================

/**
 * Fetch image from URL and return as ArrayBuffer
 *
 * @param {string} url - Image URL
 * @returns {Promise<ArrayBuffer|null>} ArrayBuffer or null on fetch failure
 */
async function fetchImageBuffer(url) {
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) {
    console.error(`[vips] Failed to fetch image: ${response.status} ${response.statusText}`);
    return null;
  }
  return response.arrayBuffer();
}

/**
 * Convert image source to ArrayBuffer
 *
 * @param {Blob|ArrayBuffer|string} imageSource - Image data (blob, arraybuffer, or URL)
 * @returns {Promise<ArrayBuffer|null>} ArrayBuffer or null on failure
 */
async function loadImageBuffer(imageSource) {
  if (typeof imageSource === 'string') {
    return fetchImageBuffer(imageSource);
  }
  if (imageSource instanceof Blob) {
    return imageSource.arrayBuffer();
  }
  return imageSource;
}

/**
 * Ensure image is RGB (drop alpha channel if present)
 *
 * @param {VipsImage} image - Input image
 * @returns {VipsImage} RGB image
 */
function ensureRgb(image) {
  if (image.bands === 4) {
    return image.extract_band(0, { n: 3 });
  }
  return image;
}

// =============================================================================
// Adjustment Application Helpers
// =============================================================================

/**
 * Apply basic adjustments in two phases:
 *   sRGB phase 1: exposure, tone sliders, vibrance, saturation
 *   sRGB phase 2: contrast, clarity, temperature, tint
 *
 * Processing order matches Sati's apply_adjustments() for pixel-perfect parity.
 *
 * @param {VipsImage} image - Input image
 * @param {object} adj - Adjustment values
 * @returns {VipsImage} Adjusted image
 */
function applyBasicAdjustments(image, adj) {
  // Phase 1: exposure, tone sliders, vibrance, saturation
  let result = applySrgbPhase(image, adj);

  // Phase 2: contrast, clarity, temperature, tint
  if (adj.contrast) result = applyContrast(result, adj.contrast);
  if (adj.clarity) result = applyClarity(result, adj.clarity);
  if (adj.temperature) result = applyTemperature(result, adj.temperature);
  if (adj.tint) result = applyTint(result, adj.tint);

  return result;
}

/**
 * Apply advanced adjustments (tone curve, color grading, sharpening)
 *
 * @param {Vips} vips - Vips instance
 * @param {VipsImage} image - Input image
 * @param {object} adj - Adjustment values
 * @returns {VipsImage} Adjusted image
 */
function applyAdvancedAdjustments(vips, image, adj) {
  let result = image;

  // Tone curve (LUT-based)
  if (adj.tone_curve && Array.isArray(adj.tone_curve) && adj.tone_curve.length >= 1) {
    try {
      result = applyToneCurve(vips, result, adj.tone_curve);
    } catch (e) { console.error('Tone curve failed:', e); }
  }

  // Color grading (lift/gamma/gain)
  if (adj.color_grading) {
    try {
      const cg = adj.color_grading;
      result = applyColorGrading(
        result,
        cg.lift || { r: 0, g: 0, b: 0 },
        cg.gamma || { r: 1, g: 1, b: 1 },
        cg.gain || { r: 1, g: 1, b: 1 }
      );
    } catch (e) { console.error('Color grading failed:', e); }
  }

  // Sharpening (unsharp mask)
  if (adj.sharpening) {
    try {
      const sharp = typeof adj.sharpening === 'object' ? adj.sharpening : { amount: adj.sharpening };
      result = applySharpening(result, sharp.amount || 0, sharp.radius || 0.5);
    } catch (e) { console.error('Sharpening failed:', e); }
  }

  // Dehaze (simplified dark channel prior for browser preview)
  if (adj.dehaze && adj.dehaze > 0) {
    try {
      result = applyDehaze(vips, result, adj.dehaze);
    } catch (e) { console.error('Dehaze failed:', e); }
  }

  // Note: Denoising is server-side only (NAFNet not available in browser)

  return result;
}

/**
 * Apply dehaze using simplified dark channel prior.
 * Simplified version for browser — uses contrast + saturation boost approximation
 * that visually matches the full algorithm at lower computational cost.
 *
 * @param {Vips} vips - Vips instance
 * @param {VipsImage} image - Input image
 * @param {number} amount - Dehaze strength (0 to 100)
 * @returns {VipsImage} Dehazed image
 */
function applyDehaze(vips, image, amount) {
  if (amount === 0) return image;
  const strength = amount / 100;

  // Approximate dehaze: boost contrast and saturation in hazy regions
  // This matches the visual effect of the full dark channel prior
  const contrastBoost = 1 + strength * 0.3;
  const contrastOffset = (1 - contrastBoost) * 128;
  let result = image.linear(
    [contrastBoost, contrastBoost, contrastBoost],
    [contrastOffset, contrastOffset, contrastOffset]
  );

  // Boost saturation (sRGB luma blend, memory-efficient)
  if (strength > 0.1) {
    const f = result.cast('float');
    const lu = f.extractBand(0).multiply(0.299)
      .add(f.extractBand(1).multiply(0.587))
      .add(f.extractBand(2).multiply(0.114));
    const lu3 = lu.bandjoin([lu, lu]);
    const satFactor = 1 + strength * 0.4;
    result = f.multiply(satFactor).add(lu3.multiply(1 - satFactor)).cast('uchar');
  }

  return result;
}

/**
 * Apply all adjustments to an image using wasm-vips
 *
 * @param {Blob|ArrayBuffer|string} imageSource - Image data (blob, arraybuffer, or URL)
 * @param {object} adjustments - Adjustment values
 * @returns {Promise<Blob|null>} Processed image as JPEG blob, or null if wasm-vips unavailable
 */
export async function applyVipsAdjustments(imageSource, adjustments) {
  const vips = await initVips();
  if (!vips) return null;

  const buffer = await loadImageBuffer(imageSource);
  if (!buffer) return null;

  let rawImage = vips.Image.newFromBuffer(new Uint8Array(buffer));
  rawImage = rawImage.autorot(); // Apply EXIF rotation — fixes crop coordinate alignment
  const rgbImage = ensureRgb(rawImage);

  const adj = adjustments || {};
  const basicImage = applyBasicAdjustments(rgbImage, adj);
  const image = applyAdvancedAdjustments(vips, basicImage, adj);

  const jpegBuffer = image.writeToBuffer('.jpg', { Q: 85 });
  return new Blob([jpegBuffer], { type: 'image/jpeg' });
}

/**
 * Apply adjustments and return as data URL for display
 *
 * @param {string} imageUrl - Source image URL
 * @param {object} adjustments - Adjustment values
 * @returns {Promise<string>} Data URL of processed image
 */
export async function applyAdjustmentsToDataUrl(imageUrl, adjustments) {
  const blob = await applyVipsAdjustments(imageUrl, adjustments);
  return URL.createObjectURL(blob);
}

/**
 * Cleanup: revoke object URLs when done
 */
export function revokeObjectUrl(url) {
  if (url?.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

// =============================================================================
// Default Adjustment Helpers
// =============================================================================

/**
 * Basic adjustment keys that default to 0
 */
const BASIC_ADJUSTMENT_KEYS = [
  'exposure',
  'contrast',
  'highlights',
  'shadows',
  'whites',
  'blacks',
  'clarity',
  'temperature',
  'tint',
  'vibrance',
  'saturation',
  'dehaze',
];

/**
 * Check if any basic numeric adjustment is non-zero
 *
 * @param {object} adj - Adjustment values
 * @returns {boolean} True if any basic adjustment is non-zero
 */
function hasNonZeroBasicAdjustment(adj) {
  return BASIC_ADJUSTMENT_KEYS.some(key => (adj[key] ?? 0) !== 0);
}

/**
 * Check if tone curve has custom values
 *
 * @param {Array|null|undefined} toneCurve - Tone curve points
 * @returns {boolean} True if tone curve has custom values
 */
function hasToneCurve(toneCurve) {
  return Array.isArray(toneCurve) && toneCurve.length > 0;
}

/**
 * Check if RGB values differ from neutral
 *
 * @param {object|null|undefined} rgb - RGB object {r, g, b}
 * @param {number} neutral - Neutral value (0 for lift, 1 for gamma/gain)
 * @returns {boolean} True if any channel differs from neutral
 */
function isNonNeutralRgb(rgb, neutral) {
  if (!rgb) return false;
  return rgb.r !== neutral || rgb.g !== neutral || rgb.b !== neutral;
}

/**
 * Check if color grading has non-neutral values
 *
 * @param {object|null|undefined} colorGrading - Color grading settings
 * @returns {boolean} True if color grading has non-neutral values
 */
function hasColorGrading(colorGrading) {
  if (!colorGrading) return false;
  const hasLift = isNonNeutralRgb(colorGrading.lift, 0);
  const hasGamma = isNonNeutralRgb(colorGrading.gamma, 1);
  const hasGain = isNonNeutralRgb(colorGrading.gain, 1);
  return hasLift || hasGamma || hasGain;
}

/**
 * Check if sharpening has non-zero amount
 *
 * @param {object|number|null|undefined} sharpening - Sharpening settings
 * @returns {boolean} True if sharpening amount is non-zero
 */
function hasSharpening(sharpening) {
  if (!sharpening) return false;
  const amount = typeof sharpening === 'object' ? sharpening.amount : sharpening;
  return amount !== 0;
}

/**
 * Check if adjustments are at default (no processing needed)
 */
export function isDefaultAdjustments(adj) {
  if (!adj) return true;

  if (hasNonZeroBasicAdjustment(adj)) return false;
  if (hasToneCurve(adj.tone_curve)) return false;
  if (hasColorGrading(adj.color_grading)) return false;
  if (hasSharpening(adj.sharpening)) return false;
  if (adj.denoise && adj.denoise !== 0) return false;

  return true;
}

/**
 * Default adjustment values
 */
export const DEFAULT_ADJUSTMENTS = {
  // Basic adjustments (LCH phase)
  exposure: 0,
  highlights: 0,
  shadows: 0,
  whites: 0,
  blacks: 0,
  vibrance: 0,
  saturation: 0,
  // Basic adjustments (sRGB phase)
  contrast: 0,
  clarity: 0,
  temperature: 0,
  tint: 0,
  dehaze: 0,
  // Advanced adjustments
  tone_curve: null, // Array of [x, y] points when set
  color_grading: null, // { lift: {r,g,b}, gamma: {r,g,b}, gain: {r,g,b} } when set
  sharpening: null, // { amount, radius } when set
  denoise: 0, // Server-side only (NAFNet not available in browser)
};
